import { useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dialog, DialogPortal } from "@/components/ui/dialog"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

interface ExclusiveCodePopupProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onCodeSubmit: (code: string) => void
  isLoading?: boolean
  error?: string
}

export function ExclusiveCodePopup({ 
  isOpen, 
  onOpenChange, 
  onCodeSubmit,
  isLoading = false,
  error
}: ExclusiveCodePopupProps) {
  const { t } = useTranslation()
  const [code, setCode] = useState("")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (code.trim()) {
      onCodeSubmit(code.trim())
    }
  }

  const handleClose = () => {
    setCode("")
    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        {/* Custom overlay with sheet-like dimming */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        {/* Custom content */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
            "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-foreground">
              {t('exclusiveCode.title')}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="p-2 -mr-2"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          {/* Description */}
          <div className="px-6 pb-4">
            <p className="text-sm text-muted-foreground">
              {t('exclusiveCode.description')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
            <div className="space-y-2">
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('exclusiveCode.codePlaceholder')}
                className="text-center text-lg font-mono tracking-wider"
                autoFocus
                required
                disabled={isLoading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
              size="lg"
              disabled={isLoading || !code.trim()}
            >
              {isLoading 
                ? t('common.loading')
                : t('exclusiveCode.submit')
              }
            </Button>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
} 